import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Sidebar } from '../components/Sidebar';
import { RebstockKnowledgePanel } from '../components/RebstockKnowledgePanel';
import AiAppSpaceLearning from './AiAppSpaceLearning';
import { getAdminToken, clearAdminToken } from '../utils/auth';
import { useAppContext } from '../context/AppContext';
import {
  submitLearning,
  validateLearningDocument,
  REBSTOCK_BOT_SLUG,
  AIAPPSPACE_BOT_SLUG,
} from '../api/aiLearningApi';

export default function AILearningPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const token = typeof window !== 'undefined' ? getAdminToken() : null;
  const { selectedApp, appsLoading } = useAppContext();

  const [submitting, setSubmitting] = useState(false);

  const [content, setContent] = useState('');
  const [document, setDocument] = useState(null);

  const appSlug = selectedApp?.slug || '';
  const isRebstockBot = appSlug === REBSTOCK_BOT_SLUG;
  const isAiAppSpaceBot = appSlug === AIAPPSPACE_BOT_SLUG;

  useEffect(() => {
    if (!token) navigate('/login', { replace: true });
  }, [token, navigate]);

  const handleLogout = () => {
    clearAdminToken();
    navigate('/login', { replace: true });
  };

  const clearDocument = () => {
    setDocument(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePickDocument = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validateLearningDocument(file);
    if (err) {
      toast.error(err);
      clearDocument();
      return;
    }
    setDocument(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedApp) {
      toast.error('Select an app from the sidebar.');
      return;
    }
    if (!content.trim() && !document) {
      toast.error('Please enter learning content or upload a document.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await submitLearning({
        appSlug: selectedApp.slug,
        content: content.trim(),
        document,
      });
      toast.success(res.message);
      setContent('');
      clearDocument();
    } catch (err) {
      toast.error(err.message || 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const showRebstockLayout = Boolean(selectedApp && isRebstockBot);
  const showAiAppSpaceLayout = Boolean(selectedApp && isAiAppSpaceBot);

  return (
    <div className="min-h-screen bg-surface text-white flex">
      <Sidebar onLogout={handleLogout} />
      <main className="flex-1 p-6 overflow-y-auto">
        {showAiAppSpaceLayout ? (
          <section className="max-w-5xl">
            <AiAppSpaceLearning />
          </section>
        ) : showRebstockLayout ? (
          <>
            <header className="mb-6 rounded-2xl border border-emerald-800/35 bg-gradient-to-br from-emerald-950/55 via-[#0d1815] to-black/50 px-5 py-5 sm:px-7 sm:py-6 shadow-[0_20px_80px_rgba(0,0,0,0.45)]">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-300/90 font-semibold mb-1.5">
                    AI Learning
                  </p>
                  <h1 className="text-2xl sm:text-3xl font-serif text-white tracking-tight">
                    Hotel Rebstock
                  </h1>
                  <p className="text-xs text-muted mt-2 max-w-2xl leading-relaxed">
                    Dedicated workspace for the Rebstock chatbot: base hotel knowledge, learned
                    entries, RAG merge, and reindex — wired to the Rebstock knowledge API (not the
                    generic text/document queue used by other apps).
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <span
                    className="inline-flex items-center gap-2 rounded-full border border-emerald-600/40 bg-emerald-950/40 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-200/95"
                  >
                    <span
                      className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.7)]"
                      aria-hidden
                    />
                    Knowledge base
                  </span>
                  <p className="text-[10px] text-muted text-right max-w-[200px]">
                    App is selected from the sidebar like the rest of the admin panel.
                  </p>
                </div>
              </div>
            </header>
            <section className="max-w-5xl">
              <RebstockKnowledgePanel />
            </section>
          </>
        ) : (
          <>
            <header className="mb-6">
              <h1 className="text-2xl font-serif text-white">AI Learning</h1>
              <p className="text-xs text-muted mt-1">
                {selectedApp
                  ? `Train the AI for ${selectedApp.name} using the sidebar app selector`
                  : 'Select a specific app from the sidebar — learning is applied per app'}
              </p>
            </header>

            <section className="max-w-2xl">
              {!selectedApp && (
                <div className="card py-12 text-center mb-4">
                  <p className="text-muted text-sm">
                    {'Choose an app in the sidebar (not "All Apps") to submit learning or open Hotel Rebstock knowledge tools.'}
                  </p>
                </div>
              )}

              {selectedApp && (
                <div className="card mb-4">
                  <p className="text-[10px] text-muted flex items-center gap-1.5">
                    <span
                      className="w-2 h-2 rounded-full inline-block shrink-0"
                      style={{ backgroundColor: selectedApp.color || '#3e7b6f' }}
                    />
                    {`Learning targets ${selectedApp.name}`}
                  </p>
                </div>
              )}

              {selectedApp && (
                <div className="card">
                  <div className="card-header">
                    <div>
                      <h2 className="card-title">Submit Learning Content</h2>
                      <p className="card-subtitle">
                        Provide text or upload a document (at least one required)
                      </p>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                    <div>
                      <label className="label" htmlFor="learning-content">
                        Learning content
                      </label>
                      <textarea
                        id="learning-content"
                        className="input min-h-[140px] resize-y w-full"
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="Enter facts, guidelines, or context you want the AI to learn…"
                        disabled={submitting}
                      />
                    </div>

                    <div className="flex items-center gap-3 py-1">
                      <span className="flex-1 h-px bg-panel-border" />
                      <span className="text-[10px] uppercase tracking-[0.14em] text-muted">or</span>
                      <span className="flex-1 h-px bg-panel-border" />
                    </div>

                    <div>
                      <label className="label">Upload document</label>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.txt,.doc,.docx,application/pdf,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        className="hidden"
                        onChange={handlePickDocument}
                        disabled={submitting}
                      />
                      <button
                        type="button"
                        className="btn-ghost w-full text-xs"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={submitting}
                      >
                        Choose document
                      </button>
                      <p className="text-[10px] text-muted mt-1">
                        PDF, TXT, DOC, or DOCX — max 10MB
                      </p>
                      {document && (
                        <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-panel-border px-3 py-2">
                          <div className="min-w-0">
                            <p className="text-xs text-white font-semibold truncate">{document.name}</p>
                            <p className="text-[10px] text-muted">{formatFileSize(document.size)}</p>
                          </div>
                          <button
                            type="button"
                            className="btn-ghost text-[10px] py-1 px-2 shrink-0"
                            onClick={clearDocument}
                            disabled={submitting}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={submitting || appsLoading}
                      className="btn-primary w-full mt-1"
                    >
                      {submitting ? 'Submitting…' : 'Submit learning'}
                    </button>
                  </form>
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
